import { useLayoutEffect } from "react";
import { Route } from "react-router";
import { Routes } from "react-router-dom";
import { Button } from "../../../../components/Button/Button.tsx";
import { PreserveSearchNavigate } from "../../../../components/Navigate/PreserveSearchNavigate.tsx";
import { usePreserveSearchParamsNavigate } from "../../../../hooks/usePreserveSearchParamsNavigate.ts";
import {
  ANIMATE_TOOL,
  COPY_AD_TOOL,
  CREATE_WORKSPACE,
  EDIT_ASSET_TOOL,
  EDIT_IMAGE_TOOL,
  EDIT_IMAGE_WITH_PROMPT_TOOL,
  EDIT_SHAPE_TOOL,
  EDIT_TEXT_TOOL,
  EXPAND_TOOL,
  FILL_TOOL,
  UPSCALE_TOOL,
  VARIATION_TOOL,
} from "../../../../routes.ts";
import { selectedAssetUuidStore } from "../../../boardState/stores/selectedAssetUuidStore.ts";
import { useWorkspaceUuid } from "../../../hooks/useWorkspaceUuid.ts";
import { useBoardAssets } from "../../hooks/useBoardAssets.ts";
import { useBoardUuid } from "../../hooks/useBoardUuid.ts";
import { useNavigateToWorkspace } from "../../hooks/useNavigateToWorkspace.ts";
import type { Asset } from "../../types.ts";
import { useDefaultEditTool } from "../hooks/useDefaultTool.ts";
import { lastSelectedToolStore } from "../stores/lastSelectedToolStore.ts";
import { EditContainer } from "./components/EditContainer.tsx";
import { ImageStripe } from "./components/ImageStripe/ImageStripe.tsx";
import { VideoPlaybackBar } from "./components/VideoPlaybackBar.tsx";
import { DesignerZoomControls } from "./Designer/components/DesignerZoomControls.tsx";
import { generativeFillCanvasStore } from "./Tools/EditAsset/GenerativeFill/stores/generativeFillCanvasStore.ts";

export const EditWorkspace = () => {
  const { assets } = useBoardAssets();
  return assets && <EditWorkspaceContent assets={assets} />;
};

const EditWorkspaceContent = ({ assets }: { assets: Asset[] }) => {
  const navigate = usePreserveSearchParamsNavigate();
  const workspaceUuid = useWorkspaceUuid();
  const boardUuid = useBoardUuid();

  const navigateToWorkspace = useNavigateToWorkspace();

  const lastSelectedCreateTool =
    lastSelectedToolStore.getState().lastCreateTool;

  useLayoutEffect(() => {
    if (
      assets.every(
        (asset) =>
          asset.uuid !== selectedAssetUuidStore.getState().selectedAssetUuid,
      )
    ) {
      assets
        .filter(
          (asset) =>
            asset.content.status === "SUCCEEDED" ||
            asset.content.status === "PROCESSING",
        )
        .let((it) => {
          if (it.length > 0) {
            selectedAssetUuidStore.setSelectedAssetUuid(it[0].uuid);
          } else {
            // If no assets selected, we return to create workspace
            navigateToWorkspace({
              workspace: CREATE_WORKSPACE,
            });
          }
        });
    }
  }, [assets, workspaceUuid, boardUuid, navigateToWorkspace]);

  const defaultEditTool = useDefaultEditTool();

  return (
    <div className="relative h-full flex-col gap-[48px]">
      <div className="absolute top-200 left-200 z-10">
        <Button
          iconName="ChevronLeft"
          size="md"
          variant="secondary"
          onClick={() =>
            navigate(
              lastSelectedCreateTool
                ? `../${CREATE_WORKSPACE}/${lastSelectedCreateTool}`
                : `../${CREATE_WORKSPACE}`,
            )
          }
        >
          Back
        </Button>
      </div>
      <div className="h-full flex-col-center">
        {/* FIXME: Instead of defining a content view per tool, we should use a single component `ImageContainer` for all
           edit tools instead of redefining different ones per tools. To do so:
            - Unify the use of canvas for all edit tools
            This will remove all flash when switching between tools */}
        <Routes>
          <Route index element={<EditContainer areLayersLocked={false} />} />
          <Route
            path={EDIT_ASSET_TOOL}
            element={<EditContainer areLayersLocked={false} />}
          />
          <Route
            path={EDIT_IMAGE_WITH_PROMPT_TOOL}
            element={<EditContainer areLayersLocked={false} />}
          />
          <Route
            path={VARIATION_TOOL}
            element={<EditContainer areLayersLocked={false} />}
          />
          <Route
            path={FILL_TOOL}
            element={
              <EditContainer overlayCanvasStore={generativeFillCanvasStore} />
            }
          />
          <Route
            path={UPSCALE_TOOL}
            element={<EditContainer areLayersLocked={false} />}
          />
          {/* FIXME: Currently, the edit container does not display the expansion ratio as it used to be in the past, due to conflict with designer canvas. */}
          <Route path={EXPAND_TOOL} element={<EditContainer />} />
          <Route
            path={ANIMATE_TOOL}
            element={<EditContainer areLayersLocked={false} />}
          />
          <Route
            path={EDIT_TEXT_TOOL}
            element={<EditContainer areLayersLocked={false} />}
          />
          <Route
            path={EDIT_SHAPE_TOOL}
            element={<EditContainer areLayersLocked={false} />}
          />
          <Route
            path={EDIT_IMAGE_TOOL}
            element={<EditContainer areLayersLocked={false} />}
          />
          <Route
            path={COPY_AD_TOOL}
            element={<EditContainer areLayersLocked={false} />}
          />
          <Route
            path="*"
            element={<PreserveSearchNavigate replace to={defaultEditTool} />}
          />
        </Routes>
      </div>
      <div className="absolute top-200 right-200 z-10 flex-row items-center gap-400">
        <VideoPlaybackBar />
        <DesignerZoomControls />
      </div>
      <ImageStripe />
    </div>
  );
};
